import { supabase } from './supabase';

/**
 * Syncs accessible_providers from a parent school to all its branches.
 * Use this to fix branches that were created before provider copying was implemented.
 * @param {string} parentSchoolId - UUID of the parent (HQ) school
 * @returns {Promise<{ updated: number, error?: string }>}
 */
export async function syncBranchesProvidersFromParent(parentSchoolId) {
  if (!parentSchoolId) return { updated: 0 };

  try {
    const { data: parentSchool, error: parentError } = await supabase
      .from('schools')
      .select('accessible_providers')
      .eq('id', parentSchoolId)
      .single();

    if (parentError || !parentSchool) {
      return { updated: 0, error: parentError?.message || 'Parent school not found' };
    }

    const providers = parentSchool.accessible_providers ?? [];
    const providersArray = Array.isArray(providers) ? providers : [];

    const { data: branches, error: branchesError } = await supabase
      .from('schools')
      .select('id')
      .eq('parent_school_id', parentSchoolId);

    if (branchesError || !branches?.length) {
      return { updated: 0 };
    }

    let updated = 0;
    for (const branch of branches) {
      const { error: updateError } = await supabase
        .from('schools')
        .update({ accessible_providers: providersArray })
        .eq('id', branch.id);

      if (!updateError) updated += 1;
    }

    return { updated };
  } catch (err) {
    return { updated: 0, error: err?.message };
  }
}

/**
 * Syncs accessible_providers from every parent school to all its branches.
 * Call once on admin dashboard load to fix all pre-update branches.
 * @returns {Promise<{ parentsProcessed: number, branchesUpdated: number }>}
 */
export async function syncAllBranchesProviders() {
  try {
    const { data: rows, error } = await supabase
      .from('schools')
      .select('parent_school_id')
      .not('parent_school_id', 'is', null);

    if (error || !rows?.length) {
      return { parentsProcessed: 0, branchesUpdated: 0 };
    }

    const parentIds = [...new Set(rows.map((r) => r.parent_school_id).filter(Boolean))];
    let branchesUpdated = 0;

    for (const parentId of parentIds) {
      const result = await syncBranchesProvidersFromParent(parentId);
      if (result.updated > 0) branchesUpdated += result.updated;
    }

    return { parentsProcessed: parentIds.length, branchesUpdated };
  } catch (err) {
    return { parentsProcessed: 0, branchesUpdated: 0 };
  }
}
